/**
 * Things to Do Page - Separate route for local attractions
 * 
 * Accessed via:
 * - Explore section entry point
 * - Hamburger menu navigation
 * 
 * GATED: Only accessible when PHASE_2_RELEASE=true
 * Structure only - no integrations yet
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, Utensils, Landmark, ShoppingBag, Trees } from 'lucide-react';
import TOKENS from '../data/tokens';
import PreviewBanner from '../components/PreviewBanner';
import MaintenanceScreen from '../components/MaintenanceScreen';
import { isMaintenanceMode } from '../utils/featureGates';

const ThingsToDoPage = () => {
  const navigate = useNavigate();
  
  // MAINTENANCE MODE CHECK
  if (isMaintenanceMode()) {
    return <MaintenanceScreen />;
  }
  
  // Check if preview banner will be shown (to add padding)
  const isPreview = window.location.hostname.includes('preview.emergentagent.com') ||
                    window.location.hostname.includes('localhost') ||
                    window.location.hostname.includes('127.0.0.1');
  
  return (
    <div style={{ 
      minHeight: '100vh',
      background: TOKENS.colors.heroBackground,
      paddingTop: isPreview ? 48 : 0, // Account for preview banner height
    }}>
      <PreviewBanner />
      
      {/* Simple header with back button */}
      <div style={{
        padding: '16px 24px',
        borderBottom: `1px solid ${TOKENS.colors.borderGrey}`,
        background: TOKENS.colors.cardBackground,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        <button
          onClick={() => navigate('/', { state: { bypassSplash: true } })}
          style={{
            background: 'rgba(212, 168, 83, 0.1)',
            border: '1px solid rgba(212, 168, 83, 0.3)',
            borderRadius: 24,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            color: TOKENS.colors.textGold,
            fontSize: 14,
            fontWeight: 600,
            transition: 'all 0.2s ease',
          }}
          data-testid="things-to-do-back-btn"
        >
          <ArrowLeft size={18} />
          <span>Return</span>
        </button>
      </div>
      
      {/* Page Content */}
      <div style={{ padding: '40px 24px', maxWidth: 600, margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64,
            height: 64,
            background: '#FFF3E0',
            border: `1px solid ${TOKENS.colors.borderGrey}`,
            borderRadius: TOKENS.borderRadius.xl,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Compass size={28} color={TOKENS.colors.textGold} />
          </div>
          
          <div style={{ 
            fontSize: TOKENS.fontSizes.labelSmall, 
            fontWeight: TOKENS.fontWeights.bold,
            color: TOKENS.colors.textGoldDark, 
            letterSpacing: TOKENS.letterSpacing.wide, 
            marginBottom: 8,
            textTransform: 'uppercase'
          }}>
            EXPLORE
          </div>
          <h1 style={{ 
            fontFamily: TOKENS.fonts.heading, 
            fontSize: 28, 
            fontWeight: TOKENS.fontWeights.medium, 
            color: TOKENS.colors.textBlack, 
            margin: 0,
          }}>
            Things to Do
          </h1>
          <p style={{
            fontSize: 15,
            color: TOKENS.colors.textGrey,
            marginTop: 12,
          }}>
            Make the most of your visit with local attractions and recommendations.
          </p>
        </div>
        
        {/* Category Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: 12,
          marginBottom: 24,
        }}>
          <CategoryCard icon={Utensils} title="Dining" subtitle="Restaurants nearby" />
          <CategoryCard icon={Landmark} title="Attractions" subtitle="Local highlights" />
          <CategoryCard icon={ShoppingBag} title="Shopping" subtitle="Boutiques & gifts" />
          <CategoryCard icon={Trees} title="Nature" subtitle="Scenic spots" />
        </div>
        
        {/* Notice */}
        <div style={{
          textAlign: 'center',
          marginTop: 32,
          padding: '20px 24px',
          background: 'linear-gradient(135deg, rgba(212,168,83,0.08) 0%, rgba(232,164,184,0.04) 100%)',
          borderRadius: 12,
          border: '1px solid rgba(212,168,83,0.15)',
        }}>
          <div style={{
            fontSize: 14,
            color: TOKENS.colors.textGrey,
            lineHeight: 1.6,
          }}>
            Curated recommendations will be shared in your invitation package.
          </div>
        </div>
      </div>
    </div>
  );
};

const CategoryCard = ({ icon: Icon, title, subtitle }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(212,168,83,0.6), rgba(198,167,90,0.25), rgba(212,168,83,0.6))',
    padding: 2,
    borderRadius: 14,
  }}>
    <div style={{ 
      background: TOKENS.colors.cardBackground, 
      borderRadius: 12, 
      padding: 20,
      textAlign: 'center',
      minHeight: 100,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: 44,
        height: 44,
        background: 'linear-gradient(135deg, rgba(212,168,83,0.12) 0%, rgba(232,164,184,0.08) 100%)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
      }}>
        <Icon size={20} color={TOKENS.colors.textGold} />
      </div>
      <div style={{ 
        fontSize: 15, 
        fontWeight: TOKENS.fontWeights.semiBold, 
        color: TOKENS.colors.textBlack,
        marginBottom: 4,
      }}>
        {title}
      </div>
      <div style={{ 
        fontSize: 13, 
        color: TOKENS.colors.textGrey,
      }}>
        {subtitle}
      </div>
    </div>
  </div>
);

export default ThingsToDoPage;
